import { Controller } from "stimulus"
import SlimSelect from "slim-select"

// Searchable multi-select of the input fields assigned to a record, shown in
// the Input Fields pop-out (input_fields/_picker.html.erb). Each chip is an
// assignment, saved as soon as it changes: picking a field POSTs it to
// input_fields#assign_to_object, and removing a chip DELETEs its input
// assignment once the user confirms. Both answer with turbo streams that
// replace this element, so the chips are re-rendered from the database rather
// than tracked here.
//
// One change to the assignments saves at a time, whether it comes from this
// picker or from a Remove button in the assignment table (#input-assignments-
// table, whose turbo-delete stream replaces this picker too). While a request
// is out the widget is disabled, further changes are refused and the table's
// Remove buttons are disabled, so two responses can't render out of order and
// leave an older picker or table on screen. A request that renders its stream
// leaves everything disabled until the stream replaces it; a failed one puts
// the chips back as they were rendered, re-enables everything and, for a
// picker change, shows an error.
//
// slim-select v1 renders a chip's ✕ as a bare, click-only span, so each one is
// given button semantics as it appears; Enter or Space clicks it.
//
// Usage:
//   <div data-controller="input-field-picker"
//        data-input-field-picker-assign-url="/input_fields/assign_to_object"
//        data-input-field-picker-objekt-id="12"
//        data-input-field-picker-objekt-klass="LicenseType">
//     <select multiple data-target="input-field-picker.select">
//       <option value="3" selected data-name="Bedrooms"
//               data-remove-url="/input_assignments/41">Bedrooms · number</option>
//     </select>
//     <p data-target="input-field-picker.error" hidden></p>
//   </div>
export default class extends Controller {
  static targets = ["select", "error"]

  connect() {
    // A Turbo cache snapshot may already contain SlimSelect markup from a
    // previous visit (the snapshot is taken before disconnect runs).
    this.removeStrayWidgets()

    this.slimSelect = new SlimSelect({
      select: this.selectTarget,
      beforeOnChange: (selection) => this.selectionChanging(selection)
    })
    this.renderedValues = this.slimSelect.selected()
    this.makeChipRemovalsKeyboardOperable()

    // Tear down before Turbo snapshots the page so the cached DOM holds the
    // pristine native select instead of the generated SlimSelect widget.
    this.beforeCache = () => this.teardown()
    document.addEventListener("turbo:before-cache", this.beforeCache)

    this.tableRemovalStarted = (event) => this.startTableRemoval(event)
    this.tableRemovalEnded = (event) => this.endTableRemoval(event)
    document.addEventListener("turbo-delete:start", this.tableRemovalStarted)
    document.addEventListener("turbo-delete:end", this.tableRemovalEnded)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    document.removeEventListener("turbo-delete:start", this.tableRemovalStarted)
    document.removeEventListener("turbo-delete:end", this.tableRemovalEnded)
    this.teardown()
  }

  // slim-select passes the selection a click would leave behind; returning
  // false cancels the click.
  selectionChanging(selection) {
    // A chip's ✕ still reaches here while the widget is disabled.
    if (this.saving) return false

    const next = selection.map((option) => option.value)
    const current = this.slimSelect.selected()
    const added = next.find((value) => !current.includes(value))
    const removed = current.find((value) => !next.includes(value))

    this.clearError()
    if (added) return this.assign(added)
    if (removed) return this.unassign(this.option(removed))
    return true
  }

  assign(inputId) {
    const body = new FormData()
    body.append("input_id", inputId)
    body.append("objekt_id", this.data.get("objektId"))
    body.append("objekt_klass", this.data.get("objektKlass"))

    this.save(this.data.get("assignUrl"), { method: "POST", body }, {
      failure: "That input field wasn't added. Please try again."
    })
    return true
  }

  unassign(option) {
    const { name, removeUrl } = option.dataset
    if (!removeUrl) return false
    if (!confirm(`Remove ${name}? Its visibility, required, rules and NAICS settings will be lost.`)) return false

    this.save(removeUrl, { method: "DELETE" }, {
      failure: "That input field wasn't removed. Please try again.",
      // Another tab already removed it, so trying again can't help.
      gone: "That input field was already removed. Reload the page to see the current fields."
    })
    return true
  }

  async save(url, options, messages) {
    this.startSaving()
    let status = null
    try {
      const response = await fetch(url, { ...options, headers: this.headers })
      status = response.status
      const stream = await this.streamFrom(response)
      if (!stream) throw new Error(`Unexpected response ${response.status} from ${url}`)

      // The stream replaces this picker, so it stays disabled until then.
      window.Turbo.renderStreamMessage(stream)
    } catch (error) {
      this.finishSaving()
      this.slimSelect?.set(this.renderedValues)
      this.showError(status === 404 && messages.gone ? messages.gone : messages.failure)
    }
  }

  // The turbo streams in a successful response, or null. A body without one
  // would render nothing, leaving this picker disabled and never replaced.
  async streamFrom(response) {
    if (!response.ok || !this.isTurboStream(response)) return null

    const body = await response.text()
    return body.includes("<turbo-stream") ? body : null
  }

  // A removal from the assignment table locks this picker until it ends. The
  // button is remembered because another stream may re-render its row, and
  // the end is then announced from the document rather than from the table.
  startTableRemoval(event) {
    if (!this.assignmentsTable?.contains(event.target)) return

    this.pendingTableRemoval = event.detail?.source ?? event.target
    this.startSaving()
  }

  // A removal that rendered its stream is replacing this picker.
  endTableRemoval(event) {
    if (!this.pendingTableRemoval || event.detail?.source !== this.pendingTableRemoval) return

    this.pendingTableRemoval = null
    if (!event.detail.rendered) this.finishSaving()
  }

  startSaving() {
    this.saving = true
    this.slimSelect?.disable()
    this.element.setAttribute("aria-busy", "true")
    this.disableTableRemovals(true)
  }

  finishSaving() {
    this.saving = false
    this.slimSelect?.enable()
    this.element.removeAttribute("aria-busy")
    this.disableTableRemovals(false)
  }

  disableTableRemovals(disabled) {
    const table = this.assignmentsTable
    if (!table) return

    table.querySelectorAll("[data-controller~='turbo-delete']").forEach((button) => {
      button.disabled = disabled
    })
  }

  get assignmentsTable() {
    return document.getElementById("input-assignments-table")
  }

  get headers() {
    return {
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      Accept: "text/vnd.turbo-stream.html"
    }
  }

  isTurboStream(response) {
    return (response.headers.get("Content-Type") || "").startsWith("text/vnd.turbo-stream.html")
  }

  option(value) {
    return Array.from(this.selectTarget.options).find((option) => option.value === value)
  }

  // slim-select v1 builds each chip's ✕ as a <span> with only an onclick
  // handler (slimselect.js:1525-1553), and builds chips afresh as the
  // selection changes (:1464-1518), so a keyboard-only user can't reach them.
  // Label each ✕ as it appears, and let Enter or Space click it: the click
  // runs slim-select's own handler, so the keyboard takes the same confirm and
  // save path as the mouse.
  makeChipRemovalsKeyboardOperable() {
    const widget = this.widget()
    if (!widget) return

    this.labelChipRemovals(widget)
    this.chipObserver = new MutationObserver(() => this.labelChipRemovals(widget))
    this.chipObserver.observe(widget, { childList: true, subtree: true })
    widget.addEventListener("keydown", (event) => this.chipRemovalKeydown(event))
  }

  labelChipRemovals(widget) {
    widget.querySelectorAll(".ss-value-delete:not([role])").forEach((removal) => {
      const field = removal.closest(".ss-value")?.querySelector(".ss-value-text")?.textContent
      removal.setAttribute("tabindex", "0")
      removal.setAttribute("role", "button")
      removal.setAttribute("aria-label", `Remove ${field}`)
    })
  }

  chipRemovalKeydown(event) {
    if (!event.target.classList.contains("ss-value-delete")) return
    if (event.key !== "Enter" && event.key !== " ") return

    // Space would otherwise scroll the page.
    event.preventDefault()
    event.stopPropagation()
    event.target.click()
  }

  widget() {
    return this.selectTarget.parentNode?.querySelector(".ss-main")
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  clearError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }

  teardown() {
    this.chipObserver?.disconnect()
    this.chipObserver = null
    if (this.slimSelect) {
      try {
        this.slimSelect.destroy()
      } catch (error) {
        // SlimSelect DOM may already be gone on Turbo teardown.
      }
      this.slimSelect = null
    }
    this.removeStrayWidgets()
  }

  removeStrayWidgets() {
    if (!this.hasSelectTarget) return

    const parent = this.selectTarget.parentNode
    if (!parent) return

    parent.querySelectorAll(".ss-main").forEach((widget) => widget.remove())
    this.selectTarget.style.display = ""
  }
}
